/* iter-72d — Compact "resolve inline" popover.
 *
 * Rendered above a highlighted <mark> when the reader clicks it. Gives
 * the project owner one-click Accept / Decline and lets the suggestion
 * author (or the owner) delete the note without opening the side
 * drawer. Non-owners see a read-only view of the note + a Close
 * button.
 *
 * The popover positions itself relative to the anchor element's
 * bounding rect. It closes on outside-click or Escape. The parent is
 * responsible for firing `onResolved` after a successful mutation so
 * the chapter body can be reloaded / suggestions refetched.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useAnchorPosition(anchorEl) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!anchorEl) { setPos(null); return undefined; }
    const compute = () => {
      const rect = anchorEl.getBoundingClientRect();
      setPos({ left: rect.left + rect.width / 2, top: rect.top });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [anchorEl]);
  return pos;
}

export default function InlineSuggestionPopover({
  suggestion,
  anchorEl,
  isOwner,
  currentUserId,
  onClose,
  onResolved,
}) {
  const pos = useAnchorPosition(anchorEl);
  const [busy, setBusy] = useState(null);   // null | 'accept' | 'decline' | 'delete'
  const cardRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    const onDown = (e) => {
      if (!cardRef.current) return;
      if (cardRef.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchorEl]);

  if (!suggestion || !pos) return null;

  const isAuthor = suggestion.author_user_id === currentUserId;
  const canDelete = isOwner || isAuthor;

  const run = async (action) => {
    setBusy(action);
    try {
      if (action === 'accept' || action === 'decline') {
        await axios.post(`${API}/suggestions/${suggestion.id}/${action}`, null,
                          { withCredentials: true });
        toast.success(action === 'accept' ? 'Suggestion accepted.' : 'Suggestion declined.');
      } else if (action === 'delete') {
        await axios.delete(`${API}/suggestions/${suggestion.id}`,
                            { withCredentials: true });
        toast.success('Suggestion deleted.');
      }
      onResolved?.(action, suggestion);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not ${action}.`);
    } finally {
      setBusy(null);
    }
  };

  // Clamp to the viewport so the popover never spills off-screen.
  const clampedLeft = Math.max(140, Math.min(window.innerWidth - 140, pos.left));

  return (
    <div
      ref={cardRef}
      data-testid={`inline-suggestion-popover-${suggestion.id}`}
      role="dialog"
      className="fixed z-[70] w-72 -translate-x-1/2 rounded-md border border-panel-border bg-panel p-3 shadow-xl"
      style={{ left: clampedLeft, top: Math.max(12, pos.top - 12), transform: 'translate(-50%, -100%)' }}
    >
      <div className="mb-1 flex items-center justify-between">
        <span
          className={`text-[13px] uppercase tracking-[0.22em] ${suggestion.kind === 'edit' ? 'text-amber-600' : 'text-sky-600'}`}
        >
          {suggestion.kind === 'edit' ? 'Suggested edit' : 'Note'} · {suggestion.author_name}
        </span>
        <button
          type="button"
          className="rounded p-0.5 text-muted-foreground hover:bg-secondary"
          aria-label="Close"
          onClick={onClose}
          data-testid="inline-suggestion-close"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      <div className="mb-2 rounded-sm bg-secondary/50 px-2 py-1 text-[13.5px] italic text-foreground">
        “{suggestion.anchor_text}”
      </div>

      {suggestion.kind === 'edit' && suggestion.proposed_text && (
        <div className="mb-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[13.5px] text-amber-900 dark:text-amber-100"
             data-testid="inline-suggestion-proposed">
          → {suggestion.proposed_text}
        </div>
      )}

      {suggestion.note && (
        <div className="mb-2 text-[13.5px] leading-snug text-foreground/90">
          {suggestion.note}
        </div>
      )}

      <div className="flex items-center gap-2">
        {isOwner ? (
          <>
            <Button
              size="sm"
              className="h-7 flex-1 gap-1 bg-emerald-600 px-2 text-[13px] font-semibold text-white hover:bg-emerald-700"
              disabled={busy !== null}
              onClick={() => run('accept')}
              data-testid="inline-suggestion-accept"
            >
              {busy === 'accept' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 flex-1 gap-1 px-2 text-[13px] font-semibold"
              disabled={busy !== null}
              onClick={() => run('decline')}
              data-testid="inline-suggestion-decline"
            >
              {busy === 'decline' ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              Decline
            </Button>
          </>
        ) : (
          <span className="flex-1 text-[13px] text-muted-foreground">
            Only the project owner can accept or decline.
          </span>
        )}
        {canDelete && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 px-1.5 text-[13px] text-destructive"
            disabled={busy !== null}
            onClick={() => run('delete')}
            data-testid="inline-suggestion-delete"
            title="Delete this suggestion"
          >
            {busy === 'delete' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          </Button>
        )}
      </div>
    </div>
  );
}
